import React, { useCallback, useState } from 'react';
import { Upload, Image as ImageIcon, AlertCircle, X, Camera, Globe, Check } from 'lucide-react';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';

interface UploadZoneProps {
  onImageSelected: (file: File) => void;
  /**
   * When provided and more than one file is chosen, the files are treated as
   * multiple angles of the SAME room (a batch). The user picks the hero
   * overview; the rest are anchored to it for cross-angle furniture consistency.
   */
  onImagesSelected?: (files: File[]) => void;
  /** Legacy style-swatch flow: upload a different angle to match furniture style. */
  referenceImageUrl?: string | null;
  onClearReference?: () => void;
  /**
   * Same-room layout anchor from gallery ("Stage another angle of this room").
   * Distinct from style matching — furniture layout should stay consistent.
   */
  sameRoomAnchorUrl?: string | null;
  onClearSameRoomAnchor?: () => void;
}

type PreviewItem = { file: File; url: string };

const UploadZone: React.FC<UploadZoneProps> = ({
  onImageSelected,
  onImagesSelected,
  referenceImageUrl,
  onClearReference,
  sameRoomAnchorUrl,
  onClearSameRoomAnchor,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [heroPickPreviews, setHeroPickPreviews] = useState<PreviewItem[] | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);

  // Multi-file batch is only offered for fresh uploads, not reference flows.
  const allowBatch = !!onImagesSelected && !referenceImageUrl && !sameRoomAnchorUrl;
  const isSameRoomResume = !!sameRoomAnchorUrl && !referenceImageUrl;

  const validateFile = (file: File): boolean => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Image size must be under ${MAX_FILE_SIZE_MB}MB.`);
      return false;
    }
    return true;
  };

  const revokePreviews = (items: PreviewItem[]) => {
    items.forEach((p) => URL.revokeObjectURL(p.url));
  };

  const cancelHeroPick = () => {
    if (heroPickPreviews) revokePreviews(heroPickPreviews);
    setHeroPickPreviews(null);
    setHeroIndex(0);
  };

  const confirmHeroPick = () => {
    if (!heroPickPreviews || !onImagesSelected) return;
    const ordered = [
      heroPickPreviews[heroIndex].file,
      ...heroPickPreviews.filter((_, i) => i !== heroIndex).map((p) => p.file),
    ];
    revokePreviews(heroPickPreviews);
    setHeroPickPreviews(null);
    setHeroIndex(0);
    onImagesSelected(ordered);
  };

  // Validate a dropped/selected list and route to the single-, batch, or hero-pick flow.
  const handleFiles = (fileList: FileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    const valid = files.filter(validateFile);
    if (valid.length === 0) return;
    if (allowBatch && valid.length > 1) {
      if (heroPickPreviews) revokePreviews(heroPickPreviews);
      setHeroPickPreviews(valid.map((file) => ({ file, url: URL.createObjectURL(file) })));
      setHeroIndex(0);
      return;
    }
    onImageSelected(valid[0]);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onImageSelected, onImagesSelected, allowBatch]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  if (heroPickPreviews && heroPickPreviews.length > 1) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full p-4 sm:p-6 animate-fade-in">
        <div className="max-w-2xl w-full space-y-5">
          <div className="text-center space-y-1.5">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-100">Pick the hero angle</h2>
            <p className="text-slate-400 text-sm sm:text-base px-2">
              Choose the best overview of the room. We&apos;ll stage this first, then match the other {heroPickPreviews.length - 1} angles to the same furniture.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {heroPickPreviews.map((preview, i) => {
              const selected = i === heroIndex;
              return (
                <button
                  key={preview.url}
                  type="button"
                  onClick={() => setHeroIndex(i)}
                  className={`relative aspect-[4/3] rounded-xl overflow-hidden border-2 transition-all ${
                    selected
                      ? 'border-purple-500 ring-2 ring-purple-500/40'
                      : 'border-slate-700 hover:border-slate-500'
                  }`}
                >
                  <img src={preview.url} alt={`Angle ${i + 1}`} className="w-full h-full object-cover" />
                  {selected && (
                    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-lg">
                      <Check size={14} />
                    </span>
                  )}
                  <span className="absolute bottom-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-md bg-black/60 text-white">
                    {selected ? 'Hero' : `Angle ${i + 1}`}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={cancelHeroPick}
              className="px-5 py-2.5 rounded-xl bg-slate-800 text-slate-200 text-sm font-medium hover:bg-slate-700 border border-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmHeroPick}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm font-bold hover:from-purple-500 hover:to-violet-500"
            >
              Continue with hero
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full w-full p-4 sm:p-6 animate-fade-in">
      <div className="max-w-xl w-full text-center space-y-4 sm:space-y-8">
        {isSameRoomResume && sameRoomAnchorUrl && (
          <div className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-purple-900/20 border border-purple-500/40">
            <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-purple-500/50 shrink-0 bg-slate-900">
              <img src={sameRoomAnchorUrl} alt="Staged room" className="w-full h-full object-cover" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-purple-200">Stage another angle of this room</p>
              <p className="text-xs text-slate-400">Upload a new photo of the same room — we&apos;ll reuse the same furniture &amp; layout.</p>
            </div>
            {onClearSameRoomAnchor && (
              <button type="button" onClick={(e) => { e.preventDefault(); onClearSameRoomAnchor(); }} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 shrink-0" aria-label="Clear room anchor">
                <X size={18} />
              </button>
            )}
          </div>
        )}
        {referenceImageUrl && !isSameRoomResume && (
          <div className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-purple-900/20 border border-purple-500/40">
            <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-purple-500/50 shrink-0 bg-slate-900">
              <img src={referenceImageUrl} alt="Reference" className="w-full h-full object-cover" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-purple-200">Match this style</p>
              <p className="text-xs text-slate-400">Upload any room photo — we&apos;ll match the furniture style (not the exact layout).</p>
            </div>
            {onClearReference && (
              <button type="button" onClick={(e) => { e.preventDefault(); onClearReference(); }} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 shrink-0" aria-label="Clear reference">
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className="space-y-1.5 sm:space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-100">Virtual Staging Studio</h2>
          <p className="text-slate-400 text-sm sm:text-base px-2">
            {isSameRoomResume
              ? 'Upload another angle of the same room to keep furniture consistent.'
              : referenceImageUrl
                ? 'Upload a room photo to match the furniture style above.'
                : 'Stage a standard room photo or a 360° virtual tour panorama with furniture and decor.'}
          </p>
        </div>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-3xl sm:rounded-[2rem] p-8 sm:p-12 transition-all duration-300 ease-in-out
            flex flex-col items-center justify-center gap-3 sm:gap-4 cursor-pointer group
            ${isDragging 
              ? 'border-orange-500 bg-orange-900/20 scale-[1.02] shadow-xl shadow-orange-900/10' 
              : 'border-slate-700 bg-slate-900 hover:border-slate-600 hover:shadow-lg hover:shadow-slate-900/50'
            }
          `}
        >
          <input
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple={allowBatch}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
            onChange={handleFileInput}
          />
          
          <div className={`
            w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center transition-colors
            ${isDragging ? 'bg-orange-900/40 text-orange-400' : 'bg-slate-800 text-slate-500 group-hover:bg-slate-700 group-hover:text-orange-400'}
          `}>
            {isDragging ? <Upload size={32} className="sm:w-10 sm:h-10" /> : <ImageIcon size={32} className="sm:w-10 sm:h-10" />}
          </div>

          <div className="space-y-1">
            <p className="font-medium text-base sm:text-lg text-slate-200">
              {isDragging ? 'Drop image here' : isSameRoomResume ? 'Upload next angle' : 'Upload Room Photo or 360° Panorama'}
            </p>
            <p className="text-xs sm:text-sm text-slate-500">
              JPG, PNG, WebP up to {MAX_FILE_SIZE_MB}MB
            </p>
            {allowBatch && (
              <p className="text-xs sm:text-sm text-purple-300/90 font-medium pt-0.5">
                Tip: select several angles of the same room — you&apos;ll pick a hero overview, then we keep furniture consistent across them.
              </p>
            )}
          </div>
        </div>

        {!referenceImageUrl && !sameRoomAnchorUrl && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
            <div className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="w-9 h-9 rounded-lg bg-slate-800 text-orange-400 flex items-center justify-center shrink-0">
                <Camera size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200">Standard room photo</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Any empty or unfinished room. Stage furniture, or run a full renovation on construction shells.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="w-9 h-9 rounded-lg bg-slate-800 text-orange-400 flex items-center justify-center shrink-0">
                <Globe size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200">360° virtual tour image</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Equirectangular 2:1 panoramas auto-detected. Preview in the built-in 360° viewer.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center gap-2 text-red-400 bg-red-900/20 border border-red-900/50 p-3 rounded-lg text-sm font-medium animate-shake">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadZone;
